import { MergeMaterialVariantsInput } from "src/generated/graphqlTypes";
import { mavIdsForMerge } from "./buildMavSelectOptions";

export type MergeSelectionVariant = {
  id: string;
  code: string;
  displayName: string;
  modelNumber?: string | null;
  manufacturerUrl?: string | null;
  listing: { id: string; name?: string | null; brand?: { id: string; name: string } | null };
  materialAttributeValues: Array<{
    id: string;
    textValue: string;
    dimension: { id: string; name: string; sortOrder: number };
  }>;
  images: Array<{ id: string }>;
  featuredImage?: { id: string } | null;
};

export type MergeFieldSelections = {
  listingId: string;
  brandId: string | undefined;
  modelNumber: string | null;
  manufacturerUrl: string | null;
  /** Dimension id → selected MAV id, or remove sentinel when the dimension is omitted from the merge. */
  mavByDimensionId: Record<string, string | undefined>;
  featuredImageId?: string;
};

export function buildMergeMaterialVariantsInput(
  variants: MergeSelectionVariant[],
  fieldSelections: MergeFieldSelections,
  source: MergeMaterialVariantsInput["source"],
): MergeMaterialVariantsInput {
  return {
    materialVariantIds: variants.map((mv) => mv.id),
    selections: {
      listingId: fieldSelections.listingId,
      brandId: fieldSelections.brandId,
      modelNumber: fieldSelections.modelNumber,
      manufacturerUrl: fieldSelections.manufacturerUrl,
      featuredImageId: fieldSelections.featuredImageId,
      mavIds: mavIdsForMerge(fieldSelections.mavByDimensionId),
    },
    source,
  };
}

/** Prefer the most common non-empty value among sources for the default field selection; ties break by first occurrence. */
function mostCommonValue<T>(variants: T[], getValue: (variant: T) => string | null | undefined): string | undefined {
  if (variants.isEmpty) return undefined;
  const counts = new Map<string, { count: number; firstIndex: number; value: string }>();
  variants.forEach((variant, index) => {
    const value = getValue(variant);
    if (value === null || value === undefined || value === "") return;
    const existing = counts.get(value);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(value, { count: 1, firstIndex: index, value });
    }
  });
  if (counts.size === 0) return undefined;
  return [...counts.values()].sortBy((entry) => [-entry.count, entry.firstIndex])[0].value;
}

/** Prefer the most common MAV id among sources for the default field selection; ties break by first occurrence. */
function mostCommonMavIdForDimension(variants: MergeSelectionVariant[], dimensionId: string): string | undefined {
  const mavs = variants
    .map((mv) => mv.materialAttributeValues.find((mav) => mav.dimension.id === dimensionId))
    .compact();
  if (mavs.isEmpty) return undefined;
  const counts = new Map<string, { count: number; firstIndex: number; mavId: string }>();
  mavs.forEach((mav, index) => {
    const key = mav.textValue;
    const existing = counts.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(key, { count: 1, firstIndex: index, mavId: mav.id });
    }
  });
  return [...counts.values()].sortBy((entry) => [-entry.count, entry.firstIndex])[0].mavId;
}

function defaultFeaturedImageId(variants: MergeSelectionVariant[]): string | undefined {
  const images = variants.flatMap((mv) => mv.images);
  if (images.isEmpty) return undefined;
  const featured = variants.map((mv) => mv.featuredImage?.id).compact().first;
  return featured ?? images[0]?.id;
}

export function uniqueListings(
  variants: Array<{ code: string; displayName: string; listing: { id: string; name?: string | null } }>,
): Array<{ id: string; name: string }> {
  return variants
    .uniqueBy((mv) => mv.listing.id)
    .map((mv) => ({ id: mv.listing.id, name: mv.listing.name || mv.displayName || mv.code }));
}

export function createDefaultFieldSelections(variants: MergeSelectionVariant[]): MergeFieldSelections | undefined {
  if (variants.isEmpty) return undefined;
  const listings = uniqueListings(variants);
  const listingId = listings[0]!.id;
  const dimensions = variants
    .flatMap((mv) => mv.materialAttributeValues.map((mav) => mav.dimension))
    .uniqueBy((d) => d.id)
    .sortBy((d) => d.sortOrder);

  const mavByDimensionId: Record<string, string | undefined> = {};
  dimensions.forEach((dimension) => {
    mavByDimensionId[dimension.id] = mostCommonMavIdForDimension(variants, dimension.id);
  });

  return {
    listingId,
    brandId: mostCommonValue(variants, (mv) => mv.listing.brand?.id),
    modelNumber: mostCommonValue(variants, (mv) => mv.modelNumber) ?? null,
    manufacturerUrl: mostCommonValue(variants, (mv) => mv.manufacturerUrl) ?? null,
    mavByDimensionId,
    featuredImageId: defaultFeaturedImageId(variants),
  };
}

/**
 * Preserves existing field picks when the selected set changes (add/remove) or Apollo
 * rebroadcasts the same entities. Only replaces a pick when it is no longer available
 * among the remaining variants (or fills defaults for newly seen dimensions).
 */
export function reconcileFieldSelections(
  prev: MergeFieldSelections,
  variants: MergeSelectionVariant[],
): MergeFieldSelections | undefined {
  if (variants.isEmpty) return undefined;
  const listings = uniqueListings(variants);
  const listingIds = new Set(listings.map((l) => l.id));
  const brandIds = new Set(variants.map((mv) => mv.listing.brand?.id).compact());
  const modelNumbers = new Set(variants.map((mv) => mv.modelNumber ?? ""));
  const urls = new Set(variants.map((mv) => mv.manufacturerUrl ?? ""));
  const imageIds = new Set(variants.flatMap((mv) => mv.images.map((img) => img.id)));

  const dimensions = variants
    .flatMap((mv) => mv.materialAttributeValues.map((mav) => mav.dimension))
    .uniqueBy((d) => d.id)
    .sortBy((d) => d.sortOrder);

  const mavByDimensionId: Record<string, string | undefined> = { ...prev.mavByDimensionId };
  dimensions.forEach((dimension) => {
    if (!(dimension.id in mavByDimensionId)) {
      mavByDimensionId[dimension.id] = mostCommonMavIdForDimension(variants, dimension.id);
    }
  });

  return {
    listingId: listingIds.has(prev.listingId) ? prev.listingId : listings[0]!.id,
    brandId:
      prev.brandId && brandIds.has(prev.brandId)
        ? prev.brandId
        : mostCommonValue(variants, (mv) => mv.listing.brand?.id),
    modelNumber: modelNumbers.has(prev.modelNumber ?? "")
      ? prev.modelNumber
      : (mostCommonValue(variants, (mv) => mv.modelNumber) ?? null),
    manufacturerUrl: urls.has(prev.manufacturerUrl ?? "")
      ? prev.manufacturerUrl
      : (mostCommonValue(variants, (mv) => mv.manufacturerUrl) ?? null),
    mavByDimensionId,
    featuredImageId:
      prev.featuredImageId && imageIds.has(prev.featuredImageId)
        ? prev.featuredImageId
        : defaultFeaturedImageId(variants),
  };
}
